/**
 * @author        yontaek@zumper.com (Yon Taek Chung)
 * @copyright     Copyright (c) 2022, Zumper
 * @description   getFilterCount
 */

import { oforEach } from '@zcode/nr/es/shared/utils/utils'
import type { Filters } from '@zcode/state-type'
import deepequal from 'fast-deep-equal'
import { DEFAULT_FILTERS } from '../../constants/defaultFilters'

const EXCLUDED_SHORT_TERM_KEYS: Array<keyof Filters> = [
  'endDate',
  'guests',
  'longTerm',
  'maxPricePerBedroomPercentile',
  'minPricePerBedroomPercentile',
  'shortTerm',
  'sort',
  'startDate',
]

const EXCLUDED_LONG_TERM_KEYS: Array<keyof Filters> = [
  'bathroomsRange',
  'bedrooms',
  'cats',
  'dogs',
  'externalRating',
  'fillElevated',
  'floorplans',
  'maxPrice',
  'minActiveUnits',
  'minPrice',
  'orFeatures',
  'pets',
  'propertyTypes',
  'sort',
]

const EXCLUDED_LONG_TERM_KEYS_1440: Array<keyof Filters> = [
  'bathroomsRange',
  'bedrooms',
  'cats',
  'dogs',
  'floorplans',
  'maxPrice',
  'fillElevated',
  'minPrice',
  'orFeatures',
  'pets',
  'propertyTypes',
  'sort',
  'minActiveUnits',
]

const EXCLUDED_LONG_TERM_KEYS_1280: Array<keyof Filters> = [
  'bathroomsRange',
  'bedrooms',
  'cats',
  'dogs',
  'floorplans',
  'maxPrice',
  'fillElevated',
  'minPrice',
  'pets',
  'propertyTypes',
  'sort',
  'minActiveUnits',
]

const EXCLUDED_LONG_TERM_KEYS_992: Array<keyof Filters> = [
  'bathroomsRange',
  'bedrooms',
  'floorplans',
  'maxPrice',
  'fillElevated',
  'minPrice',
  'pets',
  'propertyTypes',
  'sort',
  'minActiveUnits',
]

/**
 * How many filters are set?
 *
 * @param filters
 */
export const getFilterCount = (filters: Filters, width?: number) => {
  let count = 0
  // default & greater than 1440px wide
  let excludedKeys = EXCLUDED_LONG_TERM_KEYS

  if (width) {
    // mobile
    if (width < 768) {
      excludedKeys = EXCLUDED_LONG_TERM_KEYS
    } else if (width < 992) {
      excludedKeys = EXCLUDED_LONG_TERM_KEYS_992
    } else if (width < 1280) {
      excludedKeys = EXCLUDED_LONG_TERM_KEYS_1280
    } else if (width < 1440) {
      excludedKeys = EXCLUDED_LONG_TERM_KEYS_1440
    }
  }

  oforEach(filters, (value, key) => {
    if (key === 'features') {
      count += filters.features.length
    } else if (
      key === 'listingAmenities' &&
      !excludedKeys.includes('listingAmenities') &&
      !!filters.listingAmenities.length
    ) {
      count += filters.listingAmenities.length
    } else if (
      key === 'buildingAmenities' &&
      !excludedKeys.includes('buildingAmenities') &&
      !!filters.buildingAmenities.length
    ) {
      count += filters.buildingAmenities.length
    } else if (
      key === 'orFeatures' &&
      !excludedKeys.includes('orFeatures') &&
      !!filters.orFeatures.length
    ) {
      // count individual deal features when not excluded
      count += filters.orFeatures[0].length
    } else if (
      key &&
      !excludedKeys.includes(key) &&
      !deepequal(value, DEFAULT_FILTERS[key as keyof typeof DEFAULT_FILTERS])
    ) {
      count += 1
    }
  })
  return count
}

export const getShortTermFilterModalCount = (filters: Filters) => {
  let count = 0

  oforEach(filters, (value, key) => {
    if (key === 'features') {
      count += filters.features.length
    } else if (
      key &&
      !EXCLUDED_SHORT_TERM_KEYS.includes(key) &&
      !deepequal(value, DEFAULT_FILTERS[key as keyof typeof DEFAULT_FILTERS])
    ) {
      count += 1
    }
  })
  return count
}
